/**
 * i18n dictionary loader — lazily loads ONLY the active language.
 *
 * Previously all 3 languages (~630 KB combined) were shipped in one chunk.
 * Now each language lives in its own file (ru.ts / uz.ts / en.ts) and is
 * dynamically imported on demand. On first paint the user downloads only
 * ~210 KB instead of ~630 KB.
 *
 * t() stays synchronous: if the dictionary for the current language hasn't
 * finished loading yet, it returns the key (same behaviour as a missing
 * translation). A version counter + subscription lets React re-render once
 * the dictionary arrives.
 */

export type Language = 'ru' | 'uz' | 'en';

const loaded: Partial<Record<Language, Record<string, string>>> = {};
const inFlight: Partial<Record<Language, Promise<Record<string, string>>>> = {};

let version = 0;
const listeners = new Set<() => void>();

function notify() {
  version++;
  listeners.forEach((fn) => {
    try { fn(); } catch { /* swallow */ }
  });
}

export function subscribeToDictionary(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function getDictionaryVersion(): number {
  return version;
}

export function getCachedDictionary(lang: Language): Record<string, string> | undefined {
  return loaded[lang];
}

export function loadDictionary(lang: Language): Promise<Record<string, string>> {
  const cached = loaded[lang];
  if (cached) return Promise.resolve(cached);
  const existing = inFlight[lang];
  if (existing) return existing;

  const p = (async () => {
    let mod: { default: Record<string, string> };
    if (lang === 'ru') mod = await import('./ru');
    else if (lang === 'uz') mod = await import('./uz');
    else mod = await import('./en');
    loaded[lang] = mod.default;
    delete inFlight[lang];
    notify();
    return mod.default;
  })();

  inFlight[lang] = p;
  return p;
}

/**
 * Backwards-compatible accessor — Proxy that returns the loaded dictionary
 * for a language or {} if not yet loaded (triggers a load lazily). Any code
 * that does `translations[lang][key]` continues to work; missing keys fall
 * through to the key itself in t().
 */
export const translations: Record<Language, Record<string, string>> = new Proxy(
  {} as Record<Language, Record<string, string>>,
  {
    get(_t, prop: string) {
      const lang = prop as Language;
      const d = loaded[lang];
      if (d) return d;
      void loadDictionary(lang);
      return {};
    },
  }
);
